<template>
  <v-container class="my-5">
    <v-layout row wrap>
      <v-flex xs12>
        <v-card class="elevation-5">
          <v-toolbar dark color="primary">
            <v-toolbar-title>
              <v-icon left dark>mdi-dog-side</v-icon>
              {{ $t('pet_registrations') || 'Registros de Mascotas' }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
              color="white"
              outlined
              class="mr-2"
              @click="exportToPDF"
              :disabled="loading || pets.length === 0"
            >
              <v-icon left>mdi-file-pdf-box</v-icon>
              {{ $t('export_to_pdf') || 'Exportar a PDF' }}
            </v-btn>
            <v-btn icon @click="fetchPets" :loading="loading">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text>
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              :label="$t('Buscar') || 'Buscar'"
              single-line
              hide-details
              class="mb-4"
              outlined
              dense
              clearable
            ></v-text-field>

            <v-data-table
              :headers="headers"
              :items="pets"
              :loading="loading"
              :search="search"
              class="elevation-1"
              :no-data-text="$t('no_records_yet') || 'No se encontraron registros'"
              :footer-props="{
                itemsPerPageOptions: [10, 25, 50, -1],
                itemsPerPageText: $t('filas_por_pagina') || 'Filas por página'
              }"
            >
              <template v-slot:item.idPet.petType="{ item }">
                <v-chip
                  small
                  :color="getPetTypeColor(item.idPet.petType)"
                  dark
                >
                  {{ translatePetType(item.idPet.petType) }}
                </v-chip>
              </template>

              <template v-slot:item.createdAt="{ item }">
                {{ formatDate(item.createdAt) }}
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import axios from 'axios'
import store from '@/store'
import JSPDF from 'jspdf'
import 'jspdf-autotable'

export default {
  name: 'PetsRegistration',
  data () {
    return {
      search: '',
      loading: false,
      pets: [],
      headers: []
    }
  },
  beforeUpdate () {
    this.headers = [
      {
        text: this.$t('pet_name') || 'Nombre de Mascota',
        align: 'start',
        sortable: true,
        value: 'idPet.name'
      },
      {
        text: this.$t('Tipo') || 'Tipo',
        align: 'center',
        value: 'idPet.petType'
      },
      {
        text: this.$t('Raza') || 'Raza',
        align: 'center',
        value: 'idPet.breed'
      },
      {
        text: this.$t('Edad') || 'Edad',
        align: 'center',
        value: 'idPet.age'
      },
      {
        text: this.$t('fecha_registro') || 'Fecha de Registro',
        align: 'center',
        value: 'createdAt'
      }
    ]
  },
  mounted () {
    this.fetchPets()
  },
  methods: {
    fetchPets () {
      this.loading = true
      const config = {
        method: 'get',
        url: 'https://residencial-services.intrare.app/registrations/',
        headers: {
          'Content-Type': 'application/json',
          'authorization': 'Token ' + store.getters.getToken
        }
      }
      axios(config)
        .then(response => {
          // Store and sort by createdAt descending
          this.pets = response.data.sort((a, b) => {
            return new Date(a.createdAt) - new Date(b.createdAt)
          })
          this.loading = false
        })
        .catch(error => {
          console.error('Error fetching pets:', error)
          this.loading = false
          if (this.$store && this.$store.commit) {
            this.$store.commit('snackbar_activate_true')
            this.$store.commit('snackbar_change_color', 'red')
            this.$store.commit('snackbar_change_message', 'Error al obtener los datos')
          }
        })
    },
    formatDate (dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    translatePetType (type) {
      if (!type) return '-'
      const upperType = type.toUpperCase()
      switch (upperType) {
        case 'DOG': return 'Perro'
        case 'CAT': return 'Gato'
        case 'OTHER': return 'Otro'
        default: return type
      }
    },
    getPetTypeColor (type) {
      if (!type) return 'grey'
      const upperType = type.toUpperCase()
      switch (upperType) {
        case 'DOG': return 'blue darken-2'
        case 'CAT': return 'orange darken-2'
        default: return 'grey'
      }
    },
    exportToPDF () {
      const doc = new JSPDF('portrait', 'pt', 'letter')
      const title = this.$t('pet_report') || 'Reporte de Mascotas Registradas'

      const tableColumn = [
        this.$t('pet_name') || 'Nombre',
        this.$t('Tipo') || 'Tipo',
        this.$t('Raza') || 'Raza',
        this.$t('Edad') || 'Edad',
        this.$t('fecha_registro') || 'Fecha Registro'
      ]

      const tableRows = []

      this.pets.forEach(registration => {
        const petData = [
          registration.idPet.name,
          this.translatePetType(registration.idPet.petType),
          registration.idPet.breed || '-',
          registration.idPet.age,
          this.formatDate(registration.createdAt)
        ]
        tableRows.push(petData)
      })

      doc.setFontSize(18)
      doc.text(title, 40, 40)
      doc.setFontSize(10)
      doc.text(`Generado el: ${new Date().toLocaleString()}`, 40, 60)

      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 80,
        theme: 'striped',
        headStyles: { fillColor: [67, 159, 70] }
      })

      doc.save(`reporte_mascotas_${new Date().toISOString().slice(0, 10)}.pdf`)
    }
  }
}
</script>

<style scoped>
.v-card {
  border-radius: 12px;
}
</style>
